// Admin v2 — Üzemeltetői kérelmek (Figma ADM_05 + 05b): függő venueRequests jóváhagyása/elutasítása,
// alatta az üzemeltetők a helyszíneikkel és tábláikkal. Jóváhagyás: a régi VenueRequests folyamata.
import React, { useCallback, useEffect, useState } from 'react';
import { addDoc, arrayUnion, collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { Icon } from '../icons';
import { fmtDate, normalizeUser, toDate } from '../data';
import { Avatar, Badge, Button, Card, Empty, Field, Modal, PageHeader, Spinner, TextArea, useToast } from '../ui';

interface Req { id: string; userId: string; name: string; username: string; avatar: string | null; email: string; venueName: string; location: string; phone: string; at: Date | null }
interface OpVenue { id: string; name: string; boards: { id: string; label: string }[] }
interface Operator { uid: string; name: string; email: string; avatar: string | null; venues: OpVenue[] }

const DEFAULT_HOURS = {
  monday: { open: '09:00', close: '22:00', closed: false },
  tuesday: { open: '09:00', close: '22:00', closed: false },
  wednesday: { open: '09:00', close: '22:00', closed: false },
  thursday: { open: '09:00', close: '22:00', closed: false },
  friday: { open: '09:00', close: '23:00', closed: false },
  saturday: { open: '10:00', close: '23:00', closed: false },
  sunday: { open: '10:00', close: '20:00', closed: false },
};

export default function OperatorsPage() {
  const toast = useToast();
  const [reqs, setReqs] = useState<Req[] | null>(null);
  const [ops, setOps] = useState<Operator[] | null>(null);
  const [rejecting, setRejecting] = useState<Req | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [uSnap, rSnap, vSnap, bSnap] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(query(collection(db, 'venueRequests'), where('status', '==', 'pending'))).catch(() => null),
      getDocs(collection(db, 'venues')).catch(() => null),
      getDocs(collection(db, 'boards')).catch(() => null),
    ]);
    const users = new Map(uSnap.docs.map((d) => [d.id, normalizeUser(d.id, d.data())]));
    setReqs((rSnap?.docs ?? []).map((d) => {
      const v = d.data();
      const u = users.get(String(v.userId ?? ''));
      return {
        id: d.id, userId: String(v.userId ?? ''), name: u?.displayName ?? 'Ismeretlen', username: u?.username ?? '',
        avatar: u?.avatarUrl ?? null, email: u?.email ?? '', venueName: String(v.venueName ?? ''), location: String(v.location ?? ''),
        phone: String(v.phone ?? ''), at: toDate(v.createdAt),
      };
    }).sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0)));

    const boards: Record<string, { id: string; label: string; n: number }[]> = {};
    bSnap?.docs.forEach((d) => {
      const v = d.data();
      if (!v.venueId) return;
      const n = Number(v.boardNumber ?? 0);
      (boards[v.venueId] ??= []).push({ id: d.id, label: n ? `${n}. tábla` : String(v.name ?? 'Tábla'), n });
    });
    const venues = (vSnap?.docs ?? []).filter((d) => d.data().isActive !== false).map((d) => ({ id: d.id, ...d.data() } as { id: string; name?: string; operatorId?: string }));
    setOps(Array.from(users.values()).filter((u) => u.isVenueOperator).map((u) => ({
      uid: u.uid, name: u.displayName, email: u.email, avatar: u.avatarUrl,
      venues: venues.filter((v) => v.operatorId === u.uid || u.venueIds.includes(v.id)).map((v) => ({
        id: v.id, name: v.name || 'Névtelen helyszín',
        boards: (boards[v.id] ?? []).sort((a, b) => a.n - b.n).map(({ id, label }) => ({ id, label })),
      })),
    })).sort((a, b) => a.name.localeCompare(b.name, 'hu')));
  }, []);
  useEffect(() => { load().catch(() => { setReqs([]); setOps([]); }); }, [load]);

  const approve = async (r: Req) => {
    setBusy(r.id);
    try {
      const city = r.location.split(',')[0].trim();
      const venue = await addDoc(collection(db, 'venues'), {
        name: r.venueName, address: r.location, city, country: 'Hungary', phone: r.phone, operatorId: r.userId,
        isActive: true, status: 'active', pricePerHour: 0, openingHours: DEFAULT_HOURS, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'users', r.userId), { isVenueOperator: true, venueId: venue.id, venueIds: arrayUnion(venue.id), updatedAt: serverTimestamp() });
      await updateDoc(doc(db, 'venueRequests', r.id), { status: 'approved', approvedAt: serverTimestamp(), venueId: venue.id, updatedAt: serverTimestamp() });
      toast('Kérelem jóváhagyva, a helyszín létrejött.');
      load();
    } catch (e) {
      console.warn('[operators] jóváhagyás', e);
      toast('A jóváhagyás nem sikerült.', 'red');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader title="Üzemeltetői kérelmek" sub="Felhasználók által beküldött üzemeltetői kérelmek kezelése" />

      <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden">
        {reqs === null ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead>
                <tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
                  <th className="pl-5 pr-4 h-[39px] font-semibold w-[22%]">Kérelmező</th>
                  <th className="px-4 font-semibold w-[26%]">Helyszín</th>
                  <th className="px-4 font-semibold w-[20%]">Kapcsolat</th>
                  <th className="px-4 font-semibold w-[12%]">Beküldve</th>
                  <th className="px-4 font-semibold w-[20%]">Műveletek</th>
                </tr>
              </thead>
              <tbody>
                {reqs.map((r) => (
                  <tr key={r.id} className="border-t border-[#31383D] h-[63px]">
                    <td className="pl-5 pr-4">
                      <div className="flex items-center gap-2.5">
                        <Avatar src={r.avatar} name={r.name} size={32} />
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-white truncate">{r.name}</div>
                          <div className="text-[12px] text-[#8a8f99] truncate">{r.username || r.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 text-[14px] text-[#B8B8C3]">{[r.venueName, r.location.split(',')[0]].filter(Boolean).join(' · ')}</td>
                    <td className="px-4 text-[14px] text-[#B8B8C3]">{r.phone || r.email || '—'}</td>
                    <td className="px-4 text-[14px] text-[#B8B8C3]">{fmtDate(r.at)}</td>
                    <td className="px-4">
                      <div className="flex items-center gap-2">
                        <button type="button" disabled={busy === r.id} onClick={() => setRejecting(r)}
                          className="h-[32px] px-3.5 rounded-full border border-[#ef4444] text-[#ef4444] text-[13px] font-semibold hover:bg-[#ef4444]/10 disabled:opacity-50">
                          Elutasítás
                        </button>
                        <button type="button" disabled={busy === r.id} onClick={() => approve(r)}
                          className="inline-flex items-center gap-1.5 h-[32px] px-3.5 rounded-full bg-[#2bc48a] text-white text-[13px] font-semibold hover:brightness-110 disabled:opacity-50">
                          <Icon name="Check" size={15} />Jóváhagyás
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {reqs.length === 0 && <Empty>Nincs függő üzemeltetői kérelem.</Empty>}
          </div>
        )}
      </div>

      <h2 className="mt-8 text-[15px] font-extrabold italic uppercase text-white">Üzemeltetők és helyszíneik</h2>
      <p className="mt-1 mb-4 text-[13px] text-[#B8B8C3]">Összes üzemeltető jogosultságú felhasználó helyszíneikkel és tábláikkal</p>

      {ops === null ? <Spinner /> : ops.length === 0 ? <Card><Empty>Még nincs üzemeltető.</Empty></Card> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
          {ops.map((o) => (
            <Card key={o.uid} className="!p-5">
              <div className="flex items-center gap-3 mb-4">
                <Avatar src={o.avatar} name={o.name} size={40} />
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-white truncate">{o.name}</div>
                  <div className="text-[12px] text-[#8a8f99] truncate">{o.email}</div>
                </div>
              </div>
              {o.venues.length === 0 && <div className="text-[13px] text-[#8a8f99]">Nincs hozzárendelt helyszín.</div>}
              <div className="flex flex-col gap-2.5">
                {o.venues.map((v) => (
                  <div key={v.id} className="p-3.5 rounded-xl bg-[#1d232a]">
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={16} className="text-[#00AFF2]" />
                      <span className="flex-1 text-[14px] font-semibold text-white truncate">{v.name}</span>
                      <Badge tone={v.boards.length ? 'cyan' : 'gray'}>{v.boards.length} tábla</Badge>
                    </div>
                    {v.boards.length ? (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {v.boards.map((b) => (
                          <span key={b.id} className="h-[24px] px-2.5 inline-flex items-center rounded-full bg-[#31383D] text-[11px] font-medium text-[#B8B8C3]">{b.label}</span>
                        ))}
                      </div>
                    ) : <div className="mt-2 text-[12px] text-[#8a8f99]">Nincs tábla</div>}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {rejecting && <RejectModal req={rejecting} onClose={() => setRejecting(null)} onDone={load} />}
    </div>
  );
}

function RejectModal({ req, onClose, onDone }: { req: Req; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title="Kérelem elutasítása"
      onClose={onClose}
      width={480}
      footer={<>
        <Button kind="ghost" onClick={onClose}>Mégse</Button>
        <Button kind="danger" disabled={busy} onClick={async () => {
          setBusy(true);
          try {
            await updateDoc(doc(db, 'venueRequests', req.id), {
              status: 'rejected', rejectReason: reason.trim() || null, rejectedAt: serverTimestamp(), updatedAt: serverTimestamp(),
            });
            toast('Kérelem elutasítva.');
            onDone();
            onClose();
          } catch {
            toast('Az elutasítás nem sikerült.', 'red');
          } finally {
            setBusy(false);
          }
        }}>Elutasítás</Button>
      </>}
    >
      <p className="text-[14px] text-[#B8B8C3] leading-relaxed mb-4">
        Biztosan elutasítja <b className="text-white">{req.name}</b> kérelmét ({req.venueName})? Ez a művelet nem vonható vissza.
      </p>
      <Field label="Indoklás (opcionális)">
        <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Pl. hiányos adatok..." />
      </Field>
    </Modal>
  );
}
